import React, { useState, useEffect } from "react";
import { Modal, Button, Alert, ButtonGroup } from "react-bootstrap";
import { fetchLivreurTimeline } from "./DataService";

const CA_OBJECTIF = 5000;

const fmtHeures = (h) => {
  const hh = Math.floor(h ?? 0);
  const mm = Math.round(((h ?? 0) - hh) * 60);
  return `${hh}h${mm > 0 ? String(mm).padStart(2, "0") : ""}`;
};

const fmtEuros = (v) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v ?? 0);

function TotauxCards({ totaux, showObjectif }) {
  return (
    <div className="d-flex flex-wrap gap-3 mb-4">
      <div className="border rounded p-2 text-center" style={{ minWidth: 100 }}>
        <div className="small text-muted">CA TTC</div>
        <strong style={{ color: showObjectif && totaux.ca_ttc >= CA_OBJECTIF ? "#28a745" : showObjectif ? "#dc3545" : undefined }}>
          {fmtEuros(totaux.ca_ttc)}
        </strong>
        {showObjectif && <div className="small text-muted">obj. {fmtEuros(CA_OBJECTIF)}</div>}
      </div>
      <div className="border rounded p-2 text-center" style={{ minWidth: 90 }}>
        <div className="small text-muted">Shifts</div>
        <strong>{totaux.nb_shifts ?? "—"}</strong>
      </div>
      <div className="border rounded p-2 text-center" style={{ minWidth: 90 }}>
        <div className="small text-muted">Livraisons</div>
        <strong>{totaux.nb_livraisons}</strong>
      </div>
      <div className="border rounded p-2 text-center" style={{ minWidth: 90 }}>
        <div className="small text-muted">Heures</div>
        <strong>{fmtHeures(totaux.heures)}</strong>
      </div>
      <div className="border rounded p-2 text-center" style={{ minWidth: 90 }}>
        <div className="small text-muted">Km</div>
        <strong>{totaux.km} km</strong>
      </div>
      <div className="border rounded p-2 text-center" style={{ minWidth: 90 }}>
        <div className="small text-muted">CA/heure</div>
        <strong>{fmtEuros(totaux.ca_par_heure)}</strong>
      </div>
    </div>
  );
}

function TimelineTable({ timeline, groupBy }) {
  if (timeline.length === 0) {
    return <p className="text-muted">Aucune activité sur cette période.</p>;
  }

  return (
    <table className="table table-sm table-hover">
      <thead className="table-light">
        <tr>
          <th>{groupBy === "week" ? "Semaine" : "Mois"}</th>
          <th className="text-end">Shifts</th>
          <th className="text-end">Heures</th>
          <th className="text-end">Km</th>
          <th className="text-end">Livraisons</th>
          <th className="text-end">CA TTC</th>
          <th className="text-end">CA/h</th>
        </tr>
      </thead>
      <tbody>
        {timeline.map((row) => (
          <tr key={row.periode}>
            <td>{row.label}</td>
            <td className="text-end">{row.nb_shifts}</td>
            <td className="text-end">{fmtHeures(row.heures)}</td>
            <td className="text-end">{row.km} km</td>
            <td className="text-end">{row.nb_livraisons}</td>
            <td className="text-end">{fmtEuros(row.ca_ttc)}</td>
            <td className="text-end">{fmtEuros(row.ca_par_heure)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function LivreurDetailModal({ livreur, month, onHide }) {
  const [groupBy, setGroupBy] = useState("week");
  const [weekData, setWeekData] = useState(null);
  const [monthData, setMonthData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Chargement vue hebdomadaire : mois sélectionné
  useEffect(() => {
    if (!livreur) return;
    const [year, m] = month.split("-");
    const from = `${year}-${m}-01`;
    const lastDay = new Date(year, parseInt(m), 0).getDate();
    const to = `${year}-${m}-${String(lastDay).padStart(2, "0")}`;

    setLoading(true);
    setError(null);
    fetchLivreurTimeline(livreur.id, from, to, "week")
      .then((data) => setWeekData(data))
      .catch(() => setError("Impossible de charger le détail."))
      .finally(() => setLoading(false));
  }, [livreur, month]);

  // Chargement vue mensuelle : année entière du mois sélectionné
  useEffect(() => {
    if (!livreur || groupBy !== "month") return;
    if (monthData) return; // déjà chargé

    const year = month.split("-")[0];
    const from = `${year}-01-01`;
    const to = `${year}-12-31`;

    setLoading(true);
    setError(null);
    fetchLivreurTimeline(livreur.id, from, to, "month")
      .then((data) => setMonthData(data))
      .catch(() => setError("Impossible de charger le détail annuel."))
      .finally(() => setLoading(false));
  }, [livreur, month, groupBy]);

  // Reset quand on change de livreur ou de mois
  useEffect(() => {
    setWeekData(null);
    setMonthData(null);
    setGroupBy("week");
  }, [livreur?.id, month]);

  const activeData = groupBy === "week" ? weekData : monthData;
  const timeline = activeData?.timeline ?? [];
  const totaux = activeData?.totaux ?? null;

  const [year] = month.split("-");

  return (
    <Modal show={!!livreur} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {livreur?.prenom} {livreur?.nom}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Toggle semaine / mois */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <span className="text-muted small">
            {groupBy === "week" ? `Détail du mois ${month}` : `Année ${year}`}
          </span>
          <ButtonGroup size="sm">
            <Button
              variant={groupBy === "week" ? "primary" : "outline-primary"}
              onClick={() => setGroupBy("week")}
            >
              Par semaine
            </Button>
            <Button
              variant={groupBy === "month" ? "primary" : "outline-primary"}
              onClick={() => setGroupBy("month")}
            >
              Par mois
            </Button>
          </ButtonGroup>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}
        {loading && <p className="text-muted">Chargement…</p>}

        {!loading && !error && (
          <>
            {totaux && (
              <TotauxCards totaux={totaux} showObjectif={groupBy === "week"} />
            )}
            <TimelineTable timeline={timeline} groupBy={groupBy} />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fermer
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default LivreurDetailModal;
